import * as React from 'react'
import styled from 'styled-components'

import { reportRpcError } from '../services/report-rpc-error'

/**
 * RpcErrorBoundary — T067 / NFR-004.
 *
 * Wraps lazy admin routes to catch uncaught render-phase errors from admin pages.
 * On error: shows a user-friendly fallback and calls `reportRpcError` with context.
 * The fallback includes a retry button that clears the error boundary state.
 *
 * Wire-up: wrap individual `LazyXxxPage` components or the `AdminShell` Outlet.
 */

const Fallback = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: ${(p) => p.theme.modalBack};
  border: 1px solid ${(p) => p.theme.warning};
  border-radius: ${(p) => p.theme.radius};
  color: ${(p) => p.theme.white};
`

const FallbackTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-family: 'NunitoSansSemiBold';
  color: ${(p) => p.theme.warning};
`

const FallbackBody = styled.p`
  margin: 0;
  font-size: 13px;
  opacity: 0.8;
  line-height: 1.5;
`

const RetryButton = styled.button`
  align-self: flex-start;
  padding: 8px 18px;
  border-radius: ${(p) => p.theme.radius};
  font-size: 13px;
  font-family: 'NunitoSansSemiBold';
  cursor: pointer;
  min-height: 36px;
  border: 1px solid ${(p) => p.theme.input.default};
  background-color: transparent;
  color: ${(p) => p.theme.white};

  &:hover { background-color: ${(p) => p.theme.input.default}; }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${(p) => p.theme.white}, 0 0 0 4px ${(p) => p.theme.accent};
  }
`

type RpcErrorBoundaryProps = {
  children: React.ReactNode
  /** Name of the admin page/section for error context. */
  pageName?: string
}

type RpcErrorBoundaryState = {
  hasError: boolean
  errorMessage: string | null
  correlationId: string | null
}

/**
 * Class-based error boundary for admin page render errors (T067).
 * Must be a class component because React error boundaries require
 * `componentDidCatch` / `getDerivedStateFromError`.
 */
export class RpcErrorBoundary extends React.Component<RpcErrorBoundaryProps, RpcErrorBoundaryState> {
  constructor(props: RpcErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, errorMessage: null, correlationId: null }
  }

  static getDerivedStateFromError(error: unknown): Partial<RpcErrorBoundaryState> {
    const message = error instanceof Error ? error.message : String(error)
    return { hasError: true, errorMessage: message }
  }

  componentDidCatch(error: unknown): void {
    const correlationId = reportRpcError(error, {
      method: `render:${this.props.pageName ?? 'admin-page'}`,
      params: undefined,
      organizationId: null,
    })
    this.setState({ correlationId })
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, errorMessage: null, correlationId: null })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <Fallback role="alert">
        <FallbackTitle>Something went wrong on this admin page</FallbackTitle>
        <FallbackBody>
          {this.state.errorMessage
            ? `Error: ${this.state.errorMessage}`
            : 'An unexpected error occurred while rendering this page.'}
          {this.state.correlationId ? (
            <span style={{ display: 'block', marginTop: 4, opacity: 0.5, fontSize: 11, fontFamily: 'monospace' }}>
              Ref: {this.state.correlationId}
            </span>
          ) : null}
        </FallbackBody>
        <RetryButton type="button" onClick={this.handleRetry} aria-label="Retry loading this page">
          Try again
        </RetryButton>
      </Fallback>
    )
  }
}
